import styled from "styled-components";
import { Link } from "react-router-dom";
import { portalColors } from "./BusinessPortalComponents";
import Button from "./Button";

export const AuthPageContent = styled.main`
    width: 100%;
    margin: 0;
    padding: 1rem;
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    place-items: center;
    box-sizing: border-box;
`;

export const AuthSurface = styled.section`
    width: min(100%, 30rem);
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(216, 222, 236, 0.9);
    border-radius: 16px;
    box-shadow: 0 24px 56px rgba(4, 8, 23, 0.28);
    padding: 2rem;
    margin: 2rem auto;
    box-sizing: border-box;

    @media (max-width: 520px) {
        padding: 1.5rem;
    }
`;

export const AuthTitle = styled.h1`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1.75rem;
    line-height: 1.2;
    font-weight: 800;
    margin: 0;
    text-align: center;
`;

export const AuthDescription = styled.p`
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9rem;
    line-height: 1.5;
    font-weight: 500;
    margin: 0.65rem 0 1.5rem;
    text-align: center;
`;

export const AuthForm = styled.form`
    display: grid;
    gap: 0.85rem;
`;

export const AuthInput = styled.input`
    width: 100%;
    height: 46px;
    padding: 0 1rem;
    color: ${portalColors.text};
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.inputBorder};
    border-radius: 8px;
    box-shadow: ${portalColors.inputShadow};
    box-sizing: border-box;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9rem;
    font-weight: 500;

    &::placeholder { color: ${portalColors.textSoft}; }
    &:focus { outline: none; border-color: ${portalColors.blue}; box-shadow: 0 0 0 4px ${portalColors.blueSoft}; }
    &:disabled { background: ${portalColors.surfaceTertiary}; cursor: not-allowed; }
`;

export const AuthPrimaryButton = styled(Button)`
    width: 100%;
    margin-top: 0.65rem;
    background: ${portalColors.blue};
    border: 1px solid ${portalColors.blue};
    color: #fff;
    box-shadow: 0 10px 22px rgba(91, 110, 245, 0.2);
`;

export const AuthPrimaryLink = styled(Link)`
    width: 100%;
    min-height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.65rem;
    padding: 0 1rem;
    box-sizing: border-box;
    border: 1px solid ${portalColors.blue};
    border-radius: 8px;
    color: #fff;
    background: ${portalColors.blue};
    box-shadow: 0 10px 22px rgba(91, 110, 245, 0.2);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    text-decoration: none;

    &:hover {
        background: ${portalColors.blueDark};
        color: #fff;
    }
`;

export const AuthSecondaryButton = styled(Button)`
    width: 100%;
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.borderStrong};
    color: ${portalColors.text};
`;

export const AuthLink = styled(Link)`
    color: ${portalColors.blue};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    text-align: center;
    text-decoration: none;

    &:hover { text-decoration: underline; }
`;

export const AuthLinks = styled.div`
    display: grid;
    gap: 0.75rem;
    margin-top: 1.25rem;
`;

export const AuthDetail = styled.div`
    padding: 0.85rem 1rem;
    color: ${portalColors.text};
    background: ${portalColors.surfaceSoft};
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
`;

export const AuthStoreLinks = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;

    a {
        display: flex;
        align-items: center;
    }

    img {
        display: block;
        width: auto;
        max-width: 100%;
        height: 3rem;
        object-fit: contain;
    }
`;

export const AuthBackButton = styled.button`
    width: 36px;
    height: 36px;
    display: inline-grid;
    place-items: center;
    padding: 0;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    cursor: pointer;

    img { width: 18px; height: 18px; }
`;

export const AuthOption = styled.button`
    width: 100%;
    min-height: 92px;
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    text-align: left;
    background: ${p => p.$selected ? portalColors.blueSoft : portalColors.surface};
    border: 1px solid ${p => p.$selected ? portalColors.blue : portalColors.border};
    border-radius: 12px;
    cursor: pointer;
    box-sizing: border-box;

    &:hover { border-color: ${portalColors.blue}; }
`;

export const AuthOptionIndicator = styled.span`
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    border: 2px solid ${p => p.$selected ? portalColors.blue : portalColors.borderStrong};
    border-radius: 50%;
    box-sizing: border-box;
    box-shadow: inset 0 0 0 ${p => p.$selected ? "4px" : "0"} ${portalColors.surface};
    background: ${p => p.$selected ? portalColors.blue : "transparent"};
`;

export const AuthOptionTitle = styled.span`
    display: block;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1rem;
    font-weight: 800;
`;

export const AuthOptionDescription = styled.span`
    display: block;
    margin-top: 0.25rem;
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.825rem;
    line-height: 1.45;
    font-weight: 500;
`;
